import { DashboardFilters, DashboardSendMessages } from '../../constants/reports'
import { ReportDashboardState, SendDashboardMessage } from '../../types/reports.types'
import { getHostFromUrl } from '@app/common/src/utils/utils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLookerEmbedURL } from '../../hooks/useLookerEmbedURL'
import { useRouter } from 'next/router'

export const useReportDashboardState = (): ReportDashboardState => {
  const { isLoading, error, embedUrl } = useLookerEmbedURL()
  const embedIFrameRef = useRef<HTMLIFrameElement>(null)
  const [dashLoaded, setDashLoaded] = useState<boolean>(false)

  const embedHost = useMemo(() => (embedUrl ? getHostFromUrl(embedUrl) : null), [embedUrl])

  useEffect(() => {
    const readLookerEvents = (event: MessageEvent): void => {
      if (event.source === embedIFrameRef.current?.contentWindow) {
        const payload = JSON.parse(event.data)
        switch (payload.type) {
          case 'dashboard:loaded': {
            setDashLoaded(true)
            break
          }
        }
      }
    }
    window.addEventListener('message', readLookerEvents)
    return () => window.removeEventListener('message', readLookerEvents)
  }, [embedIFrameRef])

  useEffect(() => {
    if (embedHost && dashLoaded) {
      embedIFrameRef.current?.contentWindow?.postMessage(
        JSON.stringify({
          type: 'dashboard:run'
        }),
        embedHost
      )
    }
  }, [dashLoaded, embedIFrameRef, embedHost])

  const sendDashboardMessage = useCallback(
    async ({ event, params }: SendDashboardMessage): Promise<void> => {
      const dashboardMessage = {
        type: event,
        ...(params && { filters: params })
      }
      if (embedHost && dashLoaded) {
        await embedIFrameRef.current?.contentWindow?.postMessage(
          JSON.stringify(dashboardMessage),
          embedHost
        )
      }
    },
    [dashLoaded, embedHost]
  )

  const { query } = useRouter()
  const deeplinkFlowcodeID = query.flowcode_id

  const updateDashboardFiltersFromQuery = useCallback(async (): Promise<void> => {
    if (!deeplinkFlowcodeID) return
    await sendDashboardMessage({
      event: DashboardSendMessages.FILTERS,
      params: {
        [DashboardFilters.BATCH_ID]: deeplinkFlowcodeID
      }
    })
    sendDashboardMessage({ event: DashboardSendMessages.RUN })
  }, [deeplinkFlowcodeID, sendDashboardMessage])

  return {
    isLoading,
    error,
    embedUrl,
    embedIFrameRef,
    sendDashboardMessage,
    updateDashboardFiltersFromQuery
  }
}
